import React, { useCallback, useEffect, useState } from 'react';
import { VisitContactPage } from './VisitExecuteScreen';
import { VisitTiming } from '../components/visits/VisitTiming';
import {
  View, Text, StyleSheet, ScrollView, TouchableOpacity, ActivityIndicator, Image, Platform,
  Modal as RNModal, TextInput,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { DirectionalIcon } from '../components/DirectionalIcon';
import { Header, FormHeader, ScreenLayout, colors, spacing, typography } from '../components';
import { Modal as AppModal } from '../components/Modal';
import { ContactSummaryCard } from '../components/ContactSummaryCard';
import { NotesSection } from '../components/NotesSection';
import { LiveVisitMap } from '../components/LiveVisitMap';
import { useLiveDirections } from '../hooks/useLiveDirections';
import { useAppContext } from '../contexts/AppContext';
import { useLocale } from '../contexts/LocaleContext';
import { createTabPressHandler } from '../navigation/handleTabPress';
import { apiService, Visit, VisitStatus, BusinessUser } from '../services/api';
import { useAppStore } from '../store/appStore';
import { getImageUrlSync } from '../utils/imageProxy';
import { useThemedStyles } from '../hooks/useThemedStyles';
import { useRTLStyles } from '../hooks/useRTLStyles';


const statusColor = (status: VisitStatus): string => {
  switch (status) {
    case 'completed':
      return colors.status.success as string;
    case 'missed':
    case 'cancelled':
      return colors.status.error as string;
    case 'in_progress':
      return colors.status.info as string;
    case 'rescheduled':
      return colors.status.warning as string;
    default:
      return colors.text.secondary as string;
  }
};

const priorityColor = (priority: Visit['priority']): string => {
  switch (priority) {
    case 'urgent':
      return colors.status.error as string;
    case 'high':
      return colors.status.warning as string;
    case 'low':
      return colors.text.secondary as string;
    default:
      return colors.primary as string;
  }
};

const statusIcon = (status: VisitStatus): keyof typeof Ionicons.glyphMap => {
  switch (status) {
    case 'completed': return 'checkmark-circle';
    case 'missed': return 'close-circle';
    case 'cancelled': return 'ban';
    case 'in_progress': return 'walk';
    case 'rescheduled': return 'calendar';
    case 'scheduled': return 'time';
    default: return 'ellipse-outline';
  }
};

const outcomeColor = (resultType: 'success' | 'failure' | 'neutral'): string => {
  switch (resultType) {
    case 'success': return colors.status.success as string;
    case 'failure': return colors.status.error as string;
    default: return colors.status.info as string;
  }
};

const outcomeIcon = (resultType: 'success' | 'failure' | 'neutral'): keyof typeof Ionicons.glyphMap => {
  switch (resultType) {
    case 'success': return 'checkmark-circle';
    case 'failure': return 'close-circle';
    default: return 'help-circle';
  }
};

/** Compact icon+label+value row for the info grid — replaces the old
 * layout's full-width "Label: value" text lines, which read as sparse and
 * left large empty gaps on wide (desktop web) viewports. */
const InfoRow: React.FC<{ icon: keyof typeof Ionicons.glyphMap; label: string; value: string }> = ({ icon, label, value }) => {
  const styles = useThemedStyles(getStyles);
  return (
  <View style={styles.infoRow}>
    <Ionicons name={icon} size={16} color={colors.text.secondary as string} />
    <View style={styles.infoRowTextWrap}>
      <Text style={styles.infoRowLabel}>{label}</Text>
      <Text style={styles.infoRowValue} numberOfLines={1}>{value}</Text>
    </View>
  </View>
);
};

const formatDateTime = (iso?: string): string => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit',
    });
  } catch {
    return iso;
  }
};

export const VisitDetailScreen: React.FC = () => {
  const styles = useThemedStyles(getStyles);
  const rtl = useRTLStyles();
  const {
    navigateTo, routeParams, showNotification, activeTab, setActiveTab,
  } = useAppContext();
  const { t } = useLocale();
  const handleTabPress = createTabPressHandler(navigateTo, setActiveTab);
  const { user: currentUser } = useAppStore();

  const visitId: string | undefined = routeParams?.visitId;

  const [visit, setVisit] = useState<Visit | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isActing, setIsActing] = useState(false);

  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [cancelReason, setCancelReason] = useState('');

  // Live DISLIX map — a real native Modal hosted directly on THIS screen
  // (not a navigation to VisitExecuteScreen) so closing it trivially returns
  // to Detail underneath, rather than falling through to a different screen.
  // Confirmed real bug with the earlier navigateTo('visit-execute') approach:
  // closing the map there fell back to Execute's own pre-start view, not
  // back to Detail. One-shot position + route fetch (not a continuous
  // watch) since this is a pre-decision preview, not live in-progress
  // tracking — that stays exclusively in VisitExecuteScreen.
  const [showLiveMapModal, setShowLiveMapModal] = useState(false);
  // Live position + road route while the fullscreen map is open; follows the agent.
  const liveDirections = useLiveDirections(visitId, showLiveMapModal);

  // Other visits with this same contact — gives the agent context (has this
  // contact been visited before, how did it go) before deciding to start.
  const [previousVisits, setPreviousVisits] = useState<Visit[]>([]);

  // assignedTo/departmentId (and each note's addedBy) are raw ids on the
  // Visit document — confirmed real usability bug: the old layout printed
  // these ids as plain text instead of a name. Resolved via the same
  // getBusinessUsers/getDepartments id->name map VisitCampaignDetailScreen
  // already builds for this exact purpose, rather than inventing a new
  // pattern.
  const [userNames, setUserNames] = useState<Record<string, string>>({});
  const [departmentNames, setDepartmentNames] = useState<Record<string, string>>({});

  // "Start next visit" quick action (post-end) — populated once the visit
  // reaches a terminal state, so the button can appear immediately rather
  // than requiring a second round-trip triggered by user action.
  const [nextVisit, setNextVisit] = useState<Visit | null>(null);

  // Contact identity — visit.contactId is only the raw omni-contact id;
  // this fetch is what powers the name/avatar header, the call/chat icons
  // (getContactDialTarget, startConversation), and ContactQuickActions
  // (Reminder / Add to List / Assign) below, none of which existed before.
  const [contact, setContact] = useState<any>(null);
  const [loadingContact, setLoadingContact] = useState(false);

  // Campaign badge (Issue F) — only fetched when the visit actually belongs
  // to one, since a single (non-campaign) visit should show nothing extra.
  const [campaignName, setCampaignName] = useState<string | null>(null);

  const loadVisit = useCallback(async () => {
    if (!visitId) return;
    setIsLoading(true);
    try {
      const res = await apiService.getVisit(visitId);
      setVisit((res?.data ?? null) as Visit | null);
    } catch (err) {
      console.warn('[VisitDetail] loadVisit error:', err);
    } finally {
      setIsLoading(false);
    }
  }, [visitId]);

  useEffect(() => {
    loadVisit();
  }, [loadVisit]);

  useEffect(() => {
    if (!visit?.contactId) {
      setContact(null);
      return;
    }
    setLoadingContact(true);
    apiService.getParentContactById(visit.contactId)
      .then((res) => setContact(res?.data ?? null))
      .catch((err) => console.warn('[VisitDetail] loadContact error:', err))
      .finally(() => setLoadingContact(false));
  }, [visit?.contactId]);

  useEffect(() => {
    if (!visit?.campaignId) {
      setCampaignName(null);
      return;
    }
    apiService.getVisitCampaign(visit.campaignId)
      .then((res) => setCampaignName(res?.data?.name ?? null))
      .catch((err) => console.warn('[VisitDetail] loadCampaign error:', err));
  }, [visit?.campaignId]);

  // Previous visits for this contact — same listVisits API this screen
  // already uses for "next visit" (line ~283), just scoped to contactId and
  // sorted most-recent-first. Capped at 5; hidden entirely if there are none.
  useEffect(() => {
    if (!visit?.contactId || !visit?.businessId) {
      setPreviousVisits([]);
      return;
    }
    let cancelled = false;
    apiService.listVisits({
      businessId: visit.businessId,
      contactId: visit.contactId,
      sortBy: 'scheduledFor',
      sortOrder: 'desc',
    })
      .then((res) => {
        if (cancelled) return;
        const others = (Array.isArray(res.data) ? res.data : []).filter((v) => v._id !== visit._id);
        setPreviousVisits(others.slice(0, 5));
      })
      .catch((err) => console.warn('[VisitDetail] loadPreviousVisits error:', err));
    return () => { cancelled = true; };
  }, [visit?.contactId, visit?.businessId, visit?._id]);

  // Resolves assignedTo/departmentId (and note.addedBy, further below) from
  // raw ids to display names — same getBusinessUsers/getDepartments pattern
  // VisitCampaignDetailScreen already uses. Loaded unconditionally on
  // mount (not gated on assignedTo/departmentId being set) since a note's
  // addedBy can reference any business user regardless of who the visit
  // itself is currently assigned to.
  useEffect(() => {
    if (!visit?.businessId) return;
    apiService.getBusinessUsers(visit.businessId)
      .then((res) => {
        if (res.statusCode !== 1000 || !Array.isArray(res.data)) return;
        const map: Record<string, string> = {};
        (res.data as BusinessUser[]).forEach((u: any) => {
          const first = u.user?.firstName || '';
          const last = u.user?.lastName || '';
          map[u.userId] = (first || last) ? `${first} ${last}`.trim() : (u.user?.email || u.userId);
        });
        setUserNames(map);
      })
      .catch((err) => console.warn('[VisitDetail] loadBusinessUsers error:', err));
    apiService.getDepartments(visit.businessId)
      .then((res) => {
        if (res.statusCode !== 1000 || !Array.isArray(res.data)) return;
        const map: Record<string, string> = {};
        (res.data as any[]).forEach((d) => { map[d._id] = d.name; });
        setDepartmentNames(map);
      })
      .catch((err) => console.warn('[VisitDetail] loadDepartments error:', err));
  }, [visit?.businessId]);

  // "Start next visit" — once this visit reaches a terminal state, look up
  // the next pending/scheduled visit assigned to the same user (or, for a
  // campaign visit, the next unstarted visit in that same campaign) so the
  // agent can move straight to it from this screen instead of navigating
  // back to a list and picking manually.
  useEffect(() => {
    const isTerminalNow = visit && (visit.status === 'completed' || visit.status === 'cancelled' || visit.status === 'missed');
    if (!isTerminalNow || !visit) {
      setNextVisit(null);
      return;
    }
    let cancelled = false;
    const filter: Record<string, any> = {
      status: 'pending,scheduled',
      sortBy: 'scheduledFor',
      sortOrder: 'asc',
      limit: 1,
    };
    if (visit.campaignId) filter.campaignId = visit.campaignId;
    else if (visit.assignedTo) filter.assignedTo = visit.assignedTo;
    // Falls back to the department this visit was assigned through — a
    // plain department-assigned visit (no campaign, no direct assignedTo)
    // previously never looked up a next visit at all, even when the same
    // department had other visits waiting to be claimed.
    else if (visit.departmentId) filter.departmentId = visit.departmentId;
    else {
      setNextVisit(null);
      return;
    }
    apiService.listVisits(filter)
      .then((res) => {
        if (cancelled) return;
        const candidate = Array.isArray(res.data) ? res.data.find((v) => v._id !== visit._id) : undefined;
        setNextVisit(candidate ?? null);
      })
      .catch((err) => console.warn('[VisitDetail] loadNextVisit error:', err));
    return () => { cancelled = true; };
  }, [visit?.status, visit?.campaignId, visit?.assignedTo, visit?.departmentId, visit?._id]);

  const contactName: string = contact?.profile?.name || t('visits.detail.unknownContact');

  // Opens the in-app DISLIX map (agent's real position + routing) as a
  // Modal directly over THIS screen — closing it lands back on Detail,
  // never a screen navigation. Does NOT start the visit; that only happens
  // via the explicit Start action (gated on location enforcement) in the
  // header below. Deliberately not an external Maps app handoff.
  const handleOpenLiveMap = useCallback(() => {
    if (!visitId || !visit?.targetLocation?.latitude || !visit?.targetLocation?.longitude) return;
    setShowLiveMapModal(true);
  }, [visitId, visit?.targetLocation]);

  const handleAddContactNote = useCallback(async (content: string) => {
    if (!contact?._id || !content.trim()) return;
    try {
      const result = await apiService.addContactNote(contact._id, content.trim());
      if (result.statusCode === 1000 && result.data) {
        setContact((prev: any) => (prev ? { ...prev, notes: result.data.notes || [] } : prev));
      }
    } catch (err) {
      console.warn('[VisitDetail] addContactNote error:', err);
    }
  }, [contact?._id]);

  const handleDeleteContactNote = useCallback(async (noteId: string | number) => {
    if (!contact?._id) return;
    try {
      const result = await apiService.deleteContactNote(contact._id, String(noteId));
      if (result.statusCode === 1000 && result.data) {
        setContact((prev: any) => (prev ? { ...prev, notes: result.data.notes || [] } : prev));
      }
    } catch (err) {
      console.warn('[VisitDetail] deleteContactNote error:', err);
    }
  }, [contact?._id]);

  const handleCancel = async () => {
    if (!visitId || !cancelReason.trim()) return;
    setIsActing(true);
    try {
      await apiService.cancelVisit(visitId, cancelReason.trim());
      setShowCancelConfirm(false);
      setCancelReason('');
      await loadVisit();
    } catch (err: any) {
      showNotification('error', t('visits.detail.title'), err.message ?? t('visits.errors.generic'));
    } finally {
      setIsActing(false);
    }
  };

  const handleClaim = async () => {
    if (!visitId) return;
    setIsActing(true);
    try {
      await apiService.claimVisit(visitId);
      await loadVisit();
    } catch (err: any) {
      showNotification('error', t('visits.detail.title'), err.message ?? t('visits.errors.generic'));
    } finally {
      setIsActing(false);
    }
  };

  if (isLoading || !visit) {
    return (
      <ScreenLayout
        header={
          <Header
            showNotifications={true}
            showMessages={true}
            onNotificationPress={() => navigateTo('notifications')}
            onMessagePress={() => navigateTo('chat')}
            showNavigation={Platform.OS === 'web'}
            activeTab={activeTab}
            onTabPress={handleTabPress}
          />
        }
        showBottomTabs={Platform.OS !== 'web'}
        onLeftPaneClose={() => {}}
        leftPaneContent="settings"
        onSettingsItemPress={async (itemId) => {
          const { setPendingSettingsItem } = await import('../utils/tempStorage');
          await setPendingSettingsItem(itemId);
          navigateTo('settings');
        }}
        showRightPane={false}
        formHeader={<FormHeader title={t('visits.detail.title')} onBack={() => navigateTo('visits')} />}
      >
        <View style={styles.screen}>
          <View style={styles.centered}>
            <ActivityIndicator color={colors.primary as string} />
          </View>
        </View>
      </ScreenLayout>
    );
  }

  const isTerminal = visit.status === 'completed' || visit.status === 'cancelled' || visit.status === 'missed';
  const canStart = visit.status === 'pending' || visit.status === 'scheduled';
  const canContinue = visit.status === 'in_progress';
  const canClaim = !!visit.departmentId && !visit.assignedTo;


  // Visits still to do / in progress are worked on the visit page — the
  // contact's profile with this visit open. The record view stays for
  // finished visits (or when explicitly asked for with `record`).
  if (!routeParams?.record && !['completed', 'missed', 'cancelled'].includes(visit.status)) {
    return <VisitContactPage />;
  }

  return (
    <ScreenLayout
      header={
        <Header
          showNotifications={true}
          showMessages={true}
          onNotificationPress={() => navigateTo('notifications')}
          onMessagePress={() => navigateTo('chat')}
          showNavigation={Platform.OS === 'web'}
          activeTab={activeTab}
          onTabPress={handleTabPress}
        />
      }
      showBottomTabs={Platform.OS !== 'web'}
      onLeftPaneClose={() => {}}
      leftPaneContent="settings"
      onSettingsItemPress={async (itemId) => {
        const { setPendingSettingsItem } = await import('../utils/tempStorage');
        await setPendingSettingsItem(itemId);
        navigateTo('settings');
      }}
      showRightPane={false}
      formHeader={
        <FormHeader
          title={t('visits.detail.title')}
          onBack={() => navigateTo('visits')}
          actions={[
            ...(canClaim ? [{ icon: 'person-add-outline' as const, label: t('visits.detail.claimVisit'), onPress: handleClaim, disabled: isActing }] : []),
            ...(canStart ? [{ icon: 'play' as const, label: t('visits.detail.startVisit'), primary: true, onPress: () => navigateTo('visit-execute', undefined, { visitId: visit._id }) }] : []),
            ...(canContinue ? [{ icon: 'arrow-forward-circle' as const, label: t('visits.detail.continueVisit'), primary: true, onPress: () => navigateTo('visit-execute', undefined, { visitId: visit._id }) }] : []),
            ...(!isTerminal ? [{ icon: 'close-circle-outline' as const, label: t('visits.detail.cancelVisit'), onPress: () => setShowCancelConfirm(true), disabled: isActing }] : []),
          ]}
        />
      }
    >
      <View style={styles.screen}>
      <ScrollView contentContainerStyle={styles.content}>
        {/* Header card — status/priority/campaign at a glance, kept compact
            (a row of icon+label chips) rather than the old layout's large
            blocks of vertical whitespace between sparse text lines. */}
        <View style={styles.card}>
          <View style={styles.badgeRow}>
            <View style={[styles.statusBadge, { borderColor: statusColor(visit.status) }]}>
              <Ionicons name={statusIcon(visit.status)} size={12} color={statusColor(visit.status)} />
              <Text style={[styles.statusBadgeText, { color: statusColor(visit.status) }]}>{t(`visits.status.${visit.status}` as any)}</Text>
            </View>
            <View style={[styles.statusBadge, { borderColor: priorityColor(visit.priority) }]}>
              <Ionicons name="flag" size={12} color={priorityColor(visit.priority)} />
              <Text style={[styles.statusBadgeText, { color: priorityColor(visit.priority) }]}>{t(`visits.priority.${visit.priority}` as any)}</Text>
            </View>
            {visit.campaignId && (
              <TouchableOpacity
                style={styles.campaignBadge}
                onPress={() => navigateTo('visit-campaign-detail', undefined, { campaignId: visit.campaignId })}
              >
                <Ionicons name="albums-outline" size={12} color={colors.primary as string} />
                <Text style={styles.campaignBadgeText} numberOfLines={1}>
                  {campaignName || t('visits.detail.partOfCampaign')}
                </Text>
              </TouchableOpacity>
            )}
          </View>
        </View>

        <ContactSummaryCard contact={contact} contactName={contactName} loadingContact={loadingContact} onNotify={showNotification} />

        {/* Info grid — the old layout spread these across many full-width
            sparse text lines; grouped into a 2-column grid of labeled,
            iconized rows instead so the same information reads at a glance
            without excess vertical scroll. */}
        <View style={styles.card}>
          <Text style={[styles.sectionTitle, { marginTop: 0 }]}>{t('visits.detail.overview')}</Text>
          <View style={styles.infoGrid}>
            <InfoRow icon="time-outline" label={t('visits.detail.expectedDuration')} value={`${visit.expectedDurationMinutes} ${t('visits.detail.minutes')}`} />
            {visit.assignedTo && <InfoRow icon="person-outline" label={t('visits.detail.assignedUser')} value={userNames[visit.assignedTo] || visit.assignedTo} />}
            {visit.departmentId && <InfoRow icon="people-outline" label={t('visits.detail.assignedDepartment')} value={departmentNames[visit.departmentId] || visit.departmentId} />}
          </View>

          <Text style={styles.sectionTitle}>{t('visits.timeline.title')}</Text>
          <VisitTiming visit={visit} timeline />

          <Text style={styles.sectionTitle}>{t('visits.detail.targetLocation')}</Text>
          {visit.targetLocation?.address ? <Text style={[styles.value, { textAlign: rtl.textStart }]}>{visit.targetLocation.address}</Text> : null}
          {typeof visit.targetLocation?.latitude === 'number' && typeof visit.targetLocation?.longitude === 'number' ? (
            <>
              {/* Static orientation thumbnail only (no live tracking, no
                  routing here). Tapping it, or the button below, opens the
                  real in-app DISLIX live map (agent's actual position +
                  routing) as a Modal over this SAME screen — see
                  handleOpenLiveMap. Never starts the visit, never hands off
                  to the device's default Maps app. */}
              <TouchableOpacity style={styles.mapPreviewWrapper} activeOpacity={0.85} onPress={handleOpenLiveMap}>
                <LiveVisitMap
                  agentPosition={null}
                  destination={{
                    latitude: visit.targetLocation.latitude,
                    longitude: visit.targetLocation.longitude,
                    address: visit.targetLocation.address,
                  }}
                  geometry={[]}
                  travelMode="driving"
                  height={160}
                />
              </TouchableOpacity>
              <TouchableOpacity style={styles.mapsButton} onPress={handleOpenLiveMap}>
                <Ionicons name="navigate-outline" size={16} color={colors.primary as string} />
                <Text style={styles.mapsButtonText}>{t('visits.detail.viewOnDislixMaps')}</Text>
              </TouchableOpacity>
              <RNModal visible={showLiveMapModal} animationType="fade" onRequestClose={() => setShowLiveMapModal(false)}>
                <View style={styles.mapFullscreenContainer}>
                  <LiveVisitMap
                    agentPosition={liveDirections.position}
                    destination={{
                      latitude: visit.targetLocation.latitude,
                      longitude: visit.targetLocation.longitude,
                      address: visit.targetLocation.address,
                    }}
                    geometry={liveDirections.geometry}
                    travelMode="driving"
                    isFullscreen
                    onClose={() => setShowLiveMapModal(false)}
                  />
                </View>
              </RNModal>
            </>
          ) : null}

          {(visit.startGeoStamp || visit.endGeoStamp) ? (
            <>
              <Text style={styles.sectionTitle}>{t('visits.detail.geoStamps')}</Text>
              {/* typeof-checked, not just truthiness of the parent object —
                  confirmed real crash: an existing visit could have a
                  startGeoStamp object present with latitude/longitude
                  themselves undefined (location enforcement was off when it
                  started), and .toFixed() on undefined threw, blanking this
                  whole screen. */}
              {visit.startGeoStamp && typeof visit.startGeoStamp.latitude === 'number' && typeof visit.startGeoStamp.longitude === 'number' ? (
                <View style={styles.iconRow}>
                  <Ionicons name="log-in-outline" size={14} color={colors.text.secondary as string} />
                  <Text style={styles.metaText}>
                    {t('visits.detail.startGeoStamp')}: {visit.startGeoStamp.latitude.toFixed(5)}, {visit.startGeoStamp.longitude.toFixed(5)}
                    {typeof visit.startGeoStamp.distanceMeters === 'number' ? ` (${Math.round(visit.startGeoStamp.distanceMeters)}m)` : ''}
                  </Text>
                </View>
              ) : null}
              {visit.endGeoStamp && typeof visit.endGeoStamp.latitude === 'number' && typeof visit.endGeoStamp.longitude === 'number' ? (
                <View style={styles.iconRow}>
                  <Ionicons name="log-out-outline" size={14} color={colors.text.secondary as string} />
                  <Text style={styles.metaText}>
                    {t('visits.detail.endGeoStamp')}: {visit.endGeoStamp.latitude.toFixed(5)}, {visit.endGeoStamp.longitude.toFixed(5)}
                    {typeof visit.endGeoStamp.distanceMeters === 'number' ? ` (${Math.round(visit.endGeoStamp.distanceMeters)}m)` : ''}
                  </Text>
                </View>
              ) : null}
            </>
          ) : null}

          {visit.photos?.length > 0 ? (
            <>
              <Text style={styles.sectionTitle}>{t('visits.detail.photos')}</Text>
              <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.photosScroll}>
                {visit.photos.map((photo, idx) => (
                  <Image
                    key={`${photo.mediaId}-${idx}`}
                    source={{ uri: getImageUrlSync(photo.url, apiService.getBaseURL?.() ?? null) }}
                    style={styles.photoThumb}
                  />
                ))}
              </ScrollView>
            </>
          ) : null}

          {visit.outcome ? (
            <>
              <Text style={styles.sectionTitle}>{t('visits.detail.outcome')}</Text>
              <View style={styles.outcomeRow}>
                <Ionicons name={outcomeIcon(visit.outcome.resultType)} size={16} color={outcomeColor(visit.outcome.resultType)} />
                <Text style={styles.value}>{visit.outcome.label}</Text>
              </View>
              {visit.outcome.actionResult?.noteText ? <Text style={styles.metaText}>{visit.outcome.actionResult.noteText}</Text> : null}
            </>
          ) : null}

          {/* What was done for the contact during the visit. */}
          {(visit.activities?.length ?? 0) > 0 ? (
            <>
              <Text style={styles.sectionTitle}>{t('visitWorkspace.activitiesTitle')}</Text>
              {visit.activities!.map((a) => (
                <View key={a.id} style={styles.outcomeRow}>
                  <Ionicons
                    name={({ order: 'cart-outline', quotation: 'document-text-outline', vendor: 'storefront-outline', reminder: 'alarm-outline', list: 'list-outline', assignment: 'person-add-outline', note: 'create-outline' } as any)[a.type] || 'ellipse-outline'}
                    size={16}
                    color={colors.primary as string}
                  />
                  <Text style={styles.value} numberOfLines={1}>
                    {t(`visitWorkspace.activity.${a.type}` as any)}{a.label ? ` · ${a.label}` : ''}{` · ${formatDateTime(a.at)}`}
                  </Text>
                </View>
              ))}
            </>
          ) : null}
        </View>

        {/* Previous visits with this same contact — gives the agent context
            before deciding to start. Hidden entirely when there are none,
            rather than showing an empty card. */}
        {previousVisits.length > 0 && (
          <View style={styles.card}>
            <Text style={styles.sectionTitle}>{t('visits.detail.previousVisits')}</Text>
            {previousVisits.map((pv) => (
              <TouchableOpacity
                key={pv._id}
                style={styles.previousVisitRow}
                onPress={() => navigateTo('visit-detail', undefined, { visitId: pv._id })}
              >
                <View style={[styles.statusStripe, { backgroundColor: statusColor(pv.status) }]} />
                <View style={styles.previousVisitBody}>
                  <Text style={styles.value} numberOfLines={1}>{formatDateTime(pv.scheduledFor)}</Text>
                  <View style={[styles.statusBadge, { borderColor: statusColor(pv.status) }]}>
                    <Ionicons name={statusIcon(pv.status)} size={11} color={statusColor(pv.status)} />
                    <Text style={[styles.statusBadgeText, { color: statusColor(pv.status) }]}>{t(`visits.status.${pv.status}` as any)}</Text>
                  </View>
                </View>
                <DirectionalIcon name="chevron-forward" size={18} color={colors.text.secondary as string} />
              </TouchableOpacity>
            ))}
          </View>
        )}

        {/* Contact notes — persistent across every interaction with this
            contact (calls, conversations, other visits), NOT scoped to this
            one visit. Already fetched as part of `contact`
            (getParentContactById), just not rendered until now. This is the
            ONLY notes section on this screen — a separate visit-scoped Notes
            card was removed as a confirmed duplicate of this one. */}
        {!!contact?._id && (
          <View style={styles.card}>
            <NotesSection
              notes={contact.notes || []}
              onAddNote={handleAddContactNote}
              onDeleteNote={handleDeleteContactNote}
              placeholder={t('visits.detail.contactNotePlaceholder')}
              title={t('visits.detail.notes')}
              currentUserId={currentUser?.id}
            />
          </View>
        )}

        {/* "Start next visit" — appears once this visit is done, so the
            agent moves straight to whatever's next (same campaign, or next
            on their own schedule) without navigating back to a list. */}
        {isTerminal && (
          <>
            {nextVisit && (
              <TouchableOpacity
                style={styles.nextVisitCard}
                onPress={() => navigateTo('visit-execute', undefined, { visitId: nextVisit._id })}
              >
                <View style={styles.nextVisitIconWrap}>
                  <DirectionalIcon name="arrow-forward-circle" size={22} color={colors.white as string} />
                </View>
                <View style={styles.nextVisitTextWrap}>
                  <Text style={styles.nextVisitTitle}>{t('visits.detail.startNextVisit')}</Text>
                  <Text style={styles.nextVisitSubtitle} numberOfLines={1}>
                    {nextVisit.targetLocation?.address || formatDateTime(nextVisit.scheduledFor)}
                  </Text>
                </View>
                <DirectionalIcon name="chevron-forward" size={18} color={colors.primary as string} />
              </TouchableOpacity>
            )}
            {/* Always offered once the visit is done, regardless of whether
                there's a next visit — an uneducated user landing here after
                finishing shouldn't be left with no obvious way out besides
                the header's back arrow. */}
            <View style={styles.postVisitActionsRow}>
              <TouchableOpacity style={styles.postVisitActionButton} onPress={() => navigateTo('visits')}>
                <Ionicons name="list-outline" size={16} color={colors.primary as string} />
                <Text style={styles.postVisitActionText}>{t('visits.detail.backToVisits')}</Text>
              </TouchableOpacity>
              <TouchableOpacity style={styles.postVisitActionButton} onPress={() => navigateTo('home')}>
                <Ionicons name="home-outline" size={16} color={colors.primary as string} />
                <Text style={styles.postVisitActionText}>{t('visits.detail.backToHome')}</Text>
              </TouchableOpacity>
            </View>
          </>
        )}

      </ScrollView>
      </View>

      {/* Cancel requires a reason — apiService.cancelVisit already accepts
          and persists one (as a visit note), the UI just never collected it
          before. The shared ConfirmationModal has no body slot for the
          reason input, so this uses the base Modal (which supports
          children/footer) instead, matching VisitsSetupScreen's own
          footer-buttons pattern. */}
      <AppModal
        visible={showCancelConfirm}
        onClose={() => { setShowCancelConfirm(false); setCancelReason(''); }}
        title={t('visits.detail.cancelConfirmTitle')}
        size="small"
        footer={
          <View style={styles.modalFooterRow}>
            <TouchableOpacity
              style={styles.modalCancelButton}
              onPress={() => { setShowCancelConfirm(false); setCancelReason(''); }}
              disabled={isActing}
            >
              <Text style={styles.modalCancelButtonText}>{t('common.cancel')}</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[styles.modalSaveButton, (!cancelReason.trim() || isActing) && styles.modalSaveButtonDisabled]}
              onPress={handleCancel}
              disabled={!cancelReason.trim() || isActing}
            >
              {isActing ? <ActivityIndicator size="small" color={colors.white as string} /> : <Text style={styles.modalSaveButtonText}>{t('common.confirm')}</Text>}
            </TouchableOpacity>
          </View>
        }
      >
        <Text style={styles.value}>{t('visits.detail.cancelConfirmMessage')}</Text>
        <Text style={[styles.sectionTitle, { marginTop: spacing.md }]}>{t('visits.detail.cancelReasonLabel')}</Text>
        <TextInput
          style={styles.cancelReasonInput}
          value={cancelReason}
          onChangeText={setCancelReason}
          placeholder={t('visits.detail.cancelReasonPlaceholder')}
          placeholderTextColor={colors.text.secondary as string}
          multiline
          numberOfLines={3}
        />
      </AppModal>
    </ScreenLayout>
  );
};

const getStyles = () => StyleSheet.create({
  screen: { flex: 1 },
  content: { padding: spacing.md, paddingBottom: spacing.xl * 2 },
  centered: { flex: 1, justifyContent: 'center', alignItems: 'center', padding: spacing.xl },

  card: {
    backgroundColor: colors.background.surface as string,
    borderRadius: 12,
    padding: spacing.md,
    borderWidth: 1,
    borderColor: colors.border as string,
    marginBottom: spacing.md,
  },
  badgeRow: { flexDirection: 'row', gap: spacing.sm, flexWrap: 'wrap' },
  statusBadge: { flexDirection: 'row', alignItems: 'center', gap: 4, borderWidth: 1, borderRadius: 12, paddingVertical: 3, paddingHorizontal: spacing.sm },
  statusBadgeText: { fontSize: typography.fontSize.sm, fontFamily: typography.fontFamily.semibold },

  sectionTitle: {
    fontSize: typography.fontSize.base,
    fontFamily: typography.fontFamily.semibold,
    color: colors.text.primary as string,
    marginTop: spacing.sm,
    marginBottom: spacing.xs,
  },
  value: { fontSize: typography.fontSize.base, fontFamily: typography.fontFamily.regular, color: colors.text.primary as string, marginBottom: 2 },
  metaText: { fontSize: typography.fontSize.sm, fontFamily: typography.fontFamily.regular, color: colors.text.secondary as string },
  iconRow: { flexDirection: 'row', alignItems: 'center', gap: spacing.xs, marginBottom: 2 },

  photosScroll: { marginTop: spacing.xs },
  photoThumb: { width: 72, height: 72, borderRadius: 8, marginRight: spacing.sm, backgroundColor: colors.background.secondary as string },

  // Dense 2-column info grid — replaces the old layout's sparse full-width
  // "Label: value" lines, which read as empty on wide (desktop web)
  // viewports.
  infoGrid: { flexDirection: 'row', flexWrap: 'wrap', gap: spacing.md, marginBottom: spacing.sm },
  infoRow: { flexDirection: 'row', alignItems: 'flex-start', gap: spacing.xs, minWidth: 200, flexGrow: 1, flexBasis: '45%' },
  infoRowTextWrap: { flex: 1 },
  infoRowLabel: { fontSize: typography.fontSize.xs, fontFamily: typography.fontFamily.regular, color: colors.text.secondary as string },
  infoRowValue: { fontSize: typography.fontSize.sm, fontFamily: typography.fontFamily.semibold, color: colors.text.primary as string },

  outcomeRow: { flexDirection: 'row', alignItems: 'center', gap: spacing.xs, marginBottom: 2 },

  nextVisitCard: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    backgroundColor: colors.background.surface as string,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: colors.primary as string,
    padding: spacing.md,
    marginBottom: spacing.lg,
  },
  nextVisitIconWrap: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: colors.primary as string,
    alignItems: 'center',
    justifyContent: 'center',
  },
  nextVisitTextWrap: { flex: 1 },
  nextVisitTitle: { fontSize: typography.fontSize.base, fontFamily: typography.fontFamily.semibold, color: colors.text.primary as string },
  nextVisitSubtitle: { fontSize: typography.fontSize.sm, fontFamily: typography.fontFamily.regular, color: colors.text.secondary as string, marginTop: 2 },

  postVisitActionsRow: { flexDirection: 'row', gap: spacing.sm, marginBottom: spacing.lg },
  postVisitActionButton: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: spacing.xs,
    borderWidth: 1,
    borderColor: colors.border as string,
    borderRadius: 12,
    paddingVertical: spacing.sm,
  },
  postVisitActionText: { fontSize: typography.fontSize.sm, fontFamily: typography.fontFamily.semibold, color: colors.primary as string },

  campaignBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs / 2,
    borderWidth: 1,
    borderColor: colors.primary as string,
    borderRadius: 12,
    paddingVertical: 2,
    paddingHorizontal: spacing.sm,
    maxWidth: 220,
  },
  campaignBadgeText: { fontSize: typography.fontSize.sm, fontFamily: typography.fontFamily.semibold, color: colors.primary as string },

  mapsButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
    alignSelf: 'flex-start',
    marginTop: spacing.sm,
    paddingVertical: spacing.xs,
    paddingHorizontal: spacing.sm,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: colors.primary as string,
  },
  mapsButtonText: { fontSize: typography.fontSize.sm, fontFamily: typography.fontFamily.semibold, color: colors.primary as string },

  mapPreviewWrapper: { marginTop: spacing.xs, borderRadius: 12, overflow: 'hidden' },
  mapFullscreenContainer: { flex: 1, backgroundColor: '#000' },

  statusStripe: { width: 4, alignSelf: 'stretch', borderRadius: 2 },
  previousVisitRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    paddingVertical: spacing.sm,
    borderTopWidth: 1,
    borderTopColor: colors.border as string,
  },
  previousVisitBody: { flex: 1, gap: 4 },

  modalFooterRow: { flexDirection: 'row', justifyContent: 'flex-end', gap: spacing.sm },
  modalCancelButton: { paddingVertical: spacing.sm, paddingHorizontal: spacing.lg, borderRadius: 8, borderWidth: 1, borderColor: colors.border as string },
  modalCancelButtonText: { fontSize: typography.fontSize.base, fontFamily: typography.fontFamily.semibold, color: colors.text.primary as string },
  modalSaveButton: { paddingVertical: spacing.sm, paddingHorizontal: spacing.lg, borderRadius: 8, backgroundColor: colors.primary as string, minWidth: 90, alignItems: 'center' },
  modalSaveButtonDisabled: { opacity: 0.5 },
  modalSaveButtonText: { fontSize: typography.fontSize.base, fontFamily: typography.fontFamily.semibold, color: colors.white as string },
  cancelReasonInput: {
    borderWidth: 1,
    borderColor: colors.border as string,
    borderRadius: 8,
    padding: spacing.sm,
    minHeight: 80,
    textAlignVertical: 'top',
    fontSize: typography.fontSize.base,
    fontFamily: typography.fontFamily.regular,
    color: colors.text.primary as string,
  },

});
